import React, { useEffect, useState } from 'react';
import {
    Box, Divider,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    TextField, Stack,
    Checkbox, Menu, MenuItem, Button,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, IconButton, Chip, FormControlLabel, Switch,
    InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { api } from '../../../services/Main/Api';
import PaginationPage from '../../../components/PaginationPage/PaginationPage';
import { useSnackbar } from 'notistack';
import { getCondoId } from '../../../services/Main/AuthStorage';
import CategoryGroupSelect from './CategoryGroupSelect';

const KIND_OPTIONS = [
    { value: 'EXPENSE', label: 'Saída', color: '#EF4444', icon: ArrowDownwardIcon },
    { value: 'INCOME', label: 'Entrada', color: '#22C55E', icon: ArrowUpwardIcon },
];

const kindLabel = (kind) => KIND_OPTIONS.find(o => o.value === kind)?.label || kind;
const kindColor = (kind) => KIND_OPTIONS.find(o => o.value === kind)?.color || '#64748B';

const headCellSx = {
    color: '#94A3B8',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E2E8F0',
};

const FinancialCategoryList = () => {
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(1);
    const [showSearch, setShowSearch] = useState(false);
    const [errors, setErrors] = useState({});
    const { enqueueSnackbar } = useSnackbar();

    const [formData, setFormData] = useState({ name: '', kind: 'EXPENSE', group: '', active: true });
    const openMenu = Boolean(anchorEl);

    useEffect(() => {
        const filters = { search, page, page_size: 50, condo_id: getCondoId() };
        api.get('/cashflow/categories/', { params: filters })
            .then(response => {
                setItems(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setSelectedIds([]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [search, page]);

    const handleSelectAll = (event) => {
        const results = items?.results || [];
        setSelectedIds(event.target.checked ? results.map((u) => u.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]);
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleDeleteClick = () => { setConfirmOpen(true); handleMenuClose(); };

    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`/cashflow/categories/${id}/`))
            );
            const updated = items.results.filter((u) => !selectedIds.includes(u.id));
            setItems((prev) => ({ ...prev, results: updated }));
            enqueueSnackbar('Categoria deletada com sucesso!', { variant: 'success' });
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Erro ao deletar categorias.', { variant: 'error' });
        } finally {
            setSelectedIds([]);
            setConfirmOpen(false);
        }
    };

    const handleRowClick = (item) => {
        setFormData({ name: item.name, kind: item.kind, group: item.group || '', active: item.active });
        setEditId(item.id);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDialogChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateFields = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Campo obrigatório';
        if (!formData.group) newErrors.group = 'Campo obrigatório';
        if (!formData.kind) newErrors.kind = 'Campo obrigatório';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const clearForm = () => {
        setFormData({ name: '', kind: 'EXPENSE', group: '', active: true });
        setErrors({});
    };

    const handleSave = () => {
        if (!validateFields()) return;
        const payload = { ...formData, condo: getCondoId() };
        if (!payload.group) payload.group = null;

        if (editMode) {
            api.put(`/cashflow/categories/${editId}/`, payload)
                .then(response => {
                    const updated = items.results.map(u => u.id === editId ? response.data : u);
                    setItems({ ...items, results: updated });
                    setDialogOpen(false);
                    clearForm();
                    setEditMode(false);
                    enqueueSnackbar('Categoria atualizada com sucesso!', { variant: 'success' });
                })
                .catch((error) => {
                    const msg = error?.response?.data;
                    if (msg) {
                        const firstError = Object.values(msg)[0];
                        enqueueSnackbar(Array.isArray(firstError) ? firstError[0] : firstError, { variant: 'error' });
                    }
                });
        } else {
            api.post('/cashflow/categories/', payload)
                .then((response) => {
                    setItems((prev) => ({ ...prev, results: [...(prev.results || []), response.data] }));
                    setDialogOpen(false);
                    clearForm();
                    enqueueSnackbar('Categoria criada com sucesso!', { variant: 'success' });
                })
                .catch((error) => {
                    const msg = error?.response?.data;
                    if (msg) {
                        const firstError = Object.values(msg)[0];
                        enqueueSnackbar(Array.isArray(firstError) ? firstError[0] : firstError, { variant: 'error' });
                    }
                });
        }
    };

    const isSelected = (id) => selectedIds.includes(id);
    const allSelected = items.results && items.results.length > 0 && selectedIds.length === items.results.length;

    return (
        <>
            <Box sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => {
                        setDialogOpen(true);
                        setEditMode(false);
                        clearForm();
                    }} sx={{
                        bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' },
                        textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2,
                    }}>Nova Categoria</Button>
                    {showSearch ? (
                        <TextField size="small" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar..." autoFocus
                            onBlur={() => { if (!search.trim()) setShowSearch(false); }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2, bgcolor: '#fff',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                },
                            }}
                        />
                    ) : (
                        <IconButton onClick={() => setShowSearch(true)} sx={{ color: '#94A3B8' }}>
                            <SearchIcon />
                        </IconButton>
                    )}
                    <Button variant="outlined" size="small" startIcon={<KeyboardArrowDownIcon />}
                        onClick={handleMenuClick} disabled={selectedIds.length === 0}
                        sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: 2,
                            borderColor: '#E2E8F0', color: '#64748B',
                            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                        }}>
                        Ações {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                    </Button>
                    <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                        <MenuItem onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1, color: '#EF4444' }} />Excluir selecionados
                        </MenuItem>
                    </Menu>
                </Stack>
            </Box>

            {loading ? (
                <Box sx={{ p: 2.5 }}>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>Carregando categorias...</Typography>
                </Box>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                <TableCell padding="checkbox" sx={headCellSx}>
                                    <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                </TableCell>
                                <TableCell sx={headCellSx}>Nome</TableCell>
                                <TableCell sx={headCellSx}>Grupo</TableCell>
                                <TableCell sx={headCellSx}>Tipo</TableCell>
                                <TableCell sx={headCellSx}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items?.results?.map((item) => {
                                const kColor = kindColor(item.kind);
                                return (
                                    <TableRow key={item.id} onClick={() => handleRowClick(item)}
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'background-color 0.15s ease',
                                            '&:hover': { bgcolor: '#F1F5F9' },
                                            '& td': { borderBottom: '1px solid #F1F5F9' },
                                        }}>
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox checked={isSelected(item.id)} onChange={() => handleSelectOne(item.id)} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                {item.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                                {item.group_detail?.name || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={kindLabel(item.kind)} size="small"
                                                sx={{
                                                    bgcolor: `${kColor}14`,
                                                    color: kColor,
                                                    fontWeight: 600, fontSize: '0.75rem', height: 26,
                                                    border: `1px solid ${kColor}30`,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={item.active ? 'Ativo' : 'Inativo'} size="small"
                                                sx={{
                                                    bgcolor: item.active ? '#22C55E14' : '#64748B14',
                                                    color: item.active ? '#22C55E' : '#64748B',
                                                    fontWeight: 600, fontSize: '0.75rem', height: 26,
                                                    border: `1px solid ${item.active ? '#22C55E30' : '#64748B30'}`,
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {items.results?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ color: '#64748B', py: 4 }}>
                                        Nenhuma categoria encontrada.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <PaginationPage data={{ ...items, page }} onChange={setPage} />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle sx={{ fontWeight: 600, color: '#1E293B' }}>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#64748B' }}>
                        Deseja excluir {selectedIds.length} {selectedIds.length === 1 ? 'categoria' : 'categorias'}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} variant="contained" sx={{
                        bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' },
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                    }}>Deletar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 600, color: '#1E293B' }}>
                    {editMode ? 'Editar Categoria' : 'Nova Categoria'}
                </DialogTitle>
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField label="Nome" name="name" value={formData.name || ''} onChange={handleDialogChange}
                        fullWidth size="small" error={!!errors.name} helperText={errors.name}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderColor: '#E2E8F0' } } }} />
                    <CategoryGroupSelect
                        value={formData.group}
                        onChange={(val) => setFormData(prev => ({ ...prev, group: val }))}
                        error={!!errors.group}
                        helperText={errors.group}
                    />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 1 }}>
                            Tipo
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {KIND_OPTIONS.map((option) => {
                                const Icon = option.icon;
                                const isSelected = formData.kind === option.value;
                                return (
                                    <Box
                                        key={option.value}
                                        onClick={() => setFormData(prev => ({ ...prev, kind: option.value }))}
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 2,
                                            border: '2px solid',
                                            borderColor: isSelected ? option.color : '#E2E8F0',
                                            bgcolor: isSelected ? `${option.color}10` : '#FFFFFF',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                borderColor: option.color,
                                                bgcolor: '#F8FAFC',
                                            },
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 32, color: isSelected ? option.color : '#94A3B8', mb: 0.5 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: isSelected ? option.color : '#1E293B' }}>
                                            {option.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                        {errors.kind && (
                            <Typography variant="caption" sx={{ color: '#EF4444', mt: 0.5 }}>
                                {errors.kind}
                            </Typography>
                        )}
                    </Box>
                    <FormControlLabel
                        control={<Switch checked={formData.active} onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} />}
                        label="Ativo"
                    />
                </DialogContent>
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                <DialogActions sx={{ py: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{
                        bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' },
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                    }}>Salvar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FinancialCategoryList;
